import React, { useMemo } from 'react';
import _isEqual from 'lodash/isEqual';
import _pickBy from 'lodash/pickBy';

import { useTranslation } from '@lyearn/i18n';
import { Box } from '@lyearn/ui';
import Frequency from '@/components/Form/components/Frequency';
import { EventUnion, ScheduleInfoInput } from '@/types/schema';

import { EventFormInput } from '../../../../types';
import {
  RECURRING_SESSION_ALLOWED_CUSTOM_FREQUENCIES,
  RECURRING_SESSION_ALLOWED_FREQUENCIES,
} from './consts';
import EndSchedule from './EndSchedule';
import { RecurrenceScheduleErrorConfig } from './utils';

interface RecurringSessionSettingsProps {
  initialEvent?: EventUnion;
  value?: ScheduleInfoInput;
  onChange: (val: ScheduleInfoInput) => void;
  disabled?: boolean;
  errorConfig?: RecurrenceScheduleErrorConfig;
  values: EventFormInput;
}

function RecurrenceSchedule({
  initialEvent,
  value,
  onChange,
  disabled,
  errorConfig,
  values,
}: RecurringSessionSettingsProps) {
  const { t } = useTranslation();

  const warnings = useMemo(() => {
    if (
      initialEvent &&
      !_isEqual(
        _pickBy(initialEvent.recurrenceSchedule, (value, key) => {
          if (key === 'endScheduleInfo') return false;
          return Boolean(value);
        }),
        _pickBy(value, (value, key) => {
          if (key === 'endScheduleInfo') return false;
          return Boolean(value);
        }),
      )
    ) {
      return [t('Events:RecurringSession.RecurrenceScheduleEditWarning')];
    }

    return undefined;
  }, [initialEvent, t, value]);

  if (!value) {
    return null;
  }

  return (
    <React.Suspense fallback={null}>
      <Box className="space-y-32">
        <Frequency
          hideTimeSelection
          allowedCustomFrequencies={RECURRING_SESSION_ALLOWED_CUSTOM_FREQUENCIES}
          allowedFrequencies={RECURRING_SESSION_ALLOWED_FREQUENCIES}
          disabled={disabled}
          errorConfig={errorConfig?.scheduleInfo}
          value={value}
          warnings={warnings}
          onChange={onChange}
        />
        <EndSchedule
          disabled={disabled}
          error={errorConfig?.endDate}
          recurrenceSchedule={values.recurrenceSchedule}
          startTime={values.startTime}
          onChange={(endScheduleInfo) => onChange({ ...value, endScheduleInfo })}
        />
      </Box>
    </React.Suspense>
  );
}

export default RecurrenceSchedule;
